/* Purchase and plan controls for the separate Complete Training Experience page. */
(() => {
  'use strict';
  const unavailable = () => new Error('This option is currently unavailable. Refresh the page or choose another plan.');
  const offers = new Set(['complete', 'shooting', 'crush-lifetime', 'crush-yearly', 'crush-monthly']);

  function positiveInteger(value) {
    const text = String(value ?? '');
    const number = Number(text);
    if (!/^\d+$/.test(text) || !Number.isSafeInteger(number) || number < 1) throw unavailable();
    return number;
  }

  function cartItems(formData, offer) {
    if (!offers.has(offer)) throw unavailable();
    const rows = new Map();
    for (const [name, value] of formData.entries()) {
      const match = /^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/.exec(name);
      if (!match) continue;
      const index = Number(match[1]);
      if (!rows.has(index)) rows.set(index, {});
      rows.get(index)[match[2]] = value;
    }
    const items = [...rows].sort(([a], [b]) => a - b).map(([index, row], expectedIndex) => {
      if (index !== expectedIndex) throw unavailable();
      const item = {
        id: positiveInteger(row.id),
        quantity: positiveInteger(row.quantity),
        properties: {_huh_landing: 'd', _huh_offer: offer}
      };
      if (row.selling_plan !== undefined) item.selling_plan = positiveInteger(row.selling_plan);
      return item;
    });
    const recurring = offer === 'crush-yearly' || offer === 'crush-monthly';
    if (recurring) {
      if (items.length !== 2 || !items[0].selling_plan || items[1].selling_plan || items[0].id === items[1].id) throw unavailable();
    } else if (items.length !== 1 || items[0].selling_plan) throw unavailable();
    return items;
  }

  function storeRoot(value) {
    return typeof value === 'string' && /^\/(?!\/)[^?#]*\/$/.test(value) ? value : '/';
  }

  function checkoutPath(base, code) {
    return `${storeRoot(base)}checkout${code ? `?discount=${encodeURIComponent(code)}` : ''}`;
  }

  function offerActive(renderedActive, expiresAt, now = Date.now()) {
    const end = Date.parse(expiresAt);
    return String(renderedActive) === 'true' && Number.isFinite(end) && now < end;
  }

  async function addPurchase(fetcher, base, formData, offer) {
    const response = await fetcher(`${storeRoot(base)}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({items: cartItems(formData, offer)})
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(typeof data.description === 'string' ? data.description : 'This selection could not be added. Please try again.');
    }
    return data;
  }

  function init(scope) {
    const roots = [...scope.querySelectorAll('[data-huh-complete]')];
    if (scope.matches?.('[data-huh-complete]')) roots.unshift(scope);
    roots.forEach(root => {
      if (root.dataset.completeInitialized) return;
      root.dataset.completeInitialized = 'true';
      const base = storeRoot(root.dataset.root);
      let busy = false;
      let pendingPurchase;
      let expiryTimer;
      const resetPurchase = () => {
        busy = false;
        if (!pendingPurchase) return;
        const {form, button, original} = pendingPurchase;
        button.innerHTML = original;
        button.disabled = false;
        form.removeAttribute('aria-busy');
        pendingPurchase = undefined;
      };
      const event = (name, offer) => {
        try {
          window.dataLayer?.push({event: name, huh_landing_variant: 'd', ...(offer ? {huh_offer: offer} : {})});
          if (typeof window.clarity === 'function') window.clarity('event', `${name}_d`);
        } catch (_) { /* Optional analytics must not interrupt a purchase. */ }
      };

      const syncOffer = () => {
        const active = offerActive(root.dataset.offerActive, root.dataset.offerExpiresAt);
        root.querySelectorAll('[data-huh-sale-only]').forEach(node => { node.hidden = !active; });
        root.querySelectorAll('[data-huh-regular-only]').forEach(node => { node.hidden = active; });
        if (!active) {
          root.dataset.offerActive = 'false';
          root.querySelectorAll('[data-huh-purchase]').forEach(form => {
            form.dataset.discount = '';
            const destination = form.querySelector('input[name="return_to"]');
            if (destination) destination.value = checkoutPath(base, '');
            const discount = form.querySelector('input[name="discount"]');
            if (discount) discount.value = '';
          });
        }
        window.clearTimeout(expiryTimer);
        if (active) {
          const remaining = Date.parse(root.dataset.offerExpiresAt) - Date.now();
          expiryTimer = window.setTimeout(syncOffer, Math.min(Math.max(remaining + 10, 10), 2147483647));
        }
        return active;
      };
      syncOffer();
      const onVisible = () => { if (!document.hidden && root.isConnected) syncOffer(); };
      document.addEventListener('visibilitychange', onVisible);
      const onPageShow = restored => {
        if (!restored.persisted) return;
        resetPurchase();
        syncOffer();
      };
      window.addEventListener('pageshow', onPageShow);

      const group = root.querySelector('[data-huh-plan-switch]');
      if (group) {
        const buttons = [...group.querySelectorAll('[data-huh-plan]')];
        const panels = [...root.querySelectorAll('[data-huh-plan-panel]')];
        const select = name => {
          buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.huhPlan === name)));
          panels.forEach(panel => { panel.hidden = panel.dataset.huhPlanPanel !== name; });
        };
        buttons.forEach((button, index) => {
          button.addEventListener('click', () => select(button.dataset.huhPlan));
          button.addEventListener('keydown', key => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key.key)) return;
            key.preventDefault();
            const nextIndex = key.key === 'Home' ? 0 : key.key === 'End' ? buttons.length - 1 : (index + (key.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
            select(buttons[nextIndex].dataset.huhPlan);
            buttons[nextIndex].focus();
          });
        });
        select('lifetime');
        group.hidden = false;
      }

      const ticker = root.querySelector('.huh-ticker');
      const tickerButton = root.querySelector('.huh-ticker-toggle');
      tickerButton?.addEventListener('click', () => {
        const paused = ticker.classList.toggle('is-paused');
        tickerButton.setAttribute('aria-pressed', String(paused));
        tickerButton.setAttribute('aria-label', `${paused ? 'Resume' : 'Pause'} scrolling highlights`);
        if (tickerButton.firstElementChild) tickerButton.firstElementChild.textContent = paused ? '▶' : 'Ⅱ';
      });

      root.querySelectorAll('[data-huh-purchase]').forEach(form => {
        form.addEventListener('submit', async submission => {
          submission.preventDefault();
          if (busy) return;
          const button = form.querySelector('button[type="submit"]');
          const error = form.querySelector('.huh-form-error');
          if (!button || button.disabled) return;
          const hadOffer = Boolean(form.dataset.discount);
          const active = syncOffer();
          if (hadOffer && !active) {
            error.textContent = 'This offer has ended. The current price is now shown. Select your option again to continue.';
            error.hidden = false;
            return;
          }
          const original = button.innerHTML;
          pendingPurchase = {form, button, original};
          busy = true;
          button.disabled = true;
          form.setAttribute('aria-busy', 'true');
          button.textContent = 'Taking you to checkout…';
          error.hidden = true;
          try {
            // Additive only: the shopper's existing items are never cleared or replaced.
            await addPurchase(window.fetch.bind(window), base, new FormData(form), form.dataset.offer);
            event('huh_product_checkout', form.dataset.offer);
            const code = offerActive(root.dataset.offerActive, root.dataset.offerExpiresAt) ? form.dataset.discount : '';
            let destination = checkoutPath(base, code);
            try {
              if (window.huhSeasonOffer?.checkoutUrl) destination = window.huhSeasonOffer.checkoutUrl(destination);
            } catch (_) { /* The direct checkout already carries this option's code. */ }
            window.location.assign(destination);
          } catch (failure) {
            error.textContent = failure instanceof TypeError ? 'Connection interrupted. Check your connection and try again.' : failure.message;
            error.hidden = false;
            resetPurchase();
          }
        });
      });
      root.querySelectorAll('[data-huh-lifetime-card]').forEach(card => {
        const offer = card.dataset.huhLifetimeCard;
        if (offer !== 'shooting' && offer !== 'crush-lifetime') return;
        const form = card.querySelector(`[data-huh-purchase][data-offer="${offer}"]`);
        const button = form?.querySelector('button[type="submit"]');
        if (!button || button.disabled || typeof form.requestSubmit !== 'function') return;
        card.dataset.huhLifetimeReady = 'true';
        card.addEventListener('click', click => {
          if (click.defaultPrevented || click.button > 0 || click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return;
          const target = click.target?.closest ? click.target : click.target?.parentElement;
          if (!target) return;
          // A plan's entire content area and native media/form interactions keep
          // their own behavior, even when the card header buys lifetime access.
          if (target.closest('video, audio, form, .huh-plan-switch, [data-huh-plan-switch], .huh-monthly-option, [data-huh-plan-panel="yearly"]')) return;
          const titleLink = target.closest('[data-huh-lifetime-link]');
          const interactive = target.closest('a, button, input, select, textarea, label, summary, details, [contenteditable], [role="button"], [role="link"]');
          if (interactive && interactive !== titleLink) return;
          const selection = window.getSelection?.();
          if (selection && !selection.isCollapsed && (card.contains(selection.anchorNode) || card.contains(selection.focusNode) || (selection.rangeCount && selection.getRangeAt(0).intersectsNode(card)))) return;
          if (busy) { click.preventDefault(); return; }
          if (button.disabled) return;
          click.preventDefault();
          // Reuse the existing offer/discount/expiry checks and additive purchase.
          form.requestSubmit(button);
        });
      });
      root.querySelectorAll('[data-huh-complete-cta]').forEach(link => {
        link.addEventListener('click', click => {
          const form = root.querySelector('[data-huh-purchase][data-offer="complete"]');
          if (!form) return;
          click.preventDefault();
          const submit = form.querySelector('button[type="submit"]');
          if (submit && !submit.disabled) form.requestSubmit(submit);
          else form.scrollIntoView({block: 'center'});
        });
      });
      event('huh_landing_view');
      document.addEventListener('shopify:section:unload', function cleanup(section) {
        if (!section.target.contains(root)) return;
        window.clearTimeout(expiryTimer);
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('pageshow', onPageShow);
        document.removeEventListener('shopify:section:unload', cleanup);
      });
    });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = {cartItems, offerActive, checkoutPath, addPurchase};
  if (typeof document !== 'undefined') {
    init(document);
    document.addEventListener('shopify:section:load', event => init(event.target));
  }
})();
